import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PriceChart from './charts/PriceChart';
import { fmtMoney, fmtPct, fmtNum, signClass } from '../utils/format';
import './ChartModal.css';

// One security, one year of context. The chart is drawn in the note's style;
// the reader's own position (shares, average cost) sits on top of it when the
// symbol is one of their holdings, and the S&P 500 can be laid alongside.

const PERIODS = [['1mo', '1M'], ['3mo', '3M'], ['6mo', '6M'], ['1y', '1Y'], ['2y', '2Y'], ['5y', '5Y']];

export default function ChartModal({ isOpen, onClose, symbol, period = '6mo', position = null, title = null, onChartRequest }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selPeriod, setSelPeriod] = useState(period);
  const [type, setType] = useState('line');
  const [bench, setBench] = useState(true);

  const sym = String(symbol || '').toUpperCase();

  const load = useCallback(async () => {
    if (!sym) return;
    setLoading(true);
    setError(null);
    try {
      const result = await onChartRequest({ symbol: sym, period: selPeriod, chart_type: 'line' });
      if (result?.chart_config?.series) setData(result.chart_config);
      else setError(result?.error || 'Could not draw that chart.');
    } catch (e) {
      setError('Could not draw that chart.');
    } finally {
      setLoading(false);
    }
  }, [sym, selPeriod, onChartRequest]);

  useEffect(() => {
    if (isOpen && sym) load();
  }, [isOpen, sym, selPeriod, load]);

  useEffect(() => {
    setSelPeriod(period);
  }, [period, sym]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const s = data?.summary;
  const cur = data?.currency || 'USD';
  const closes = data?.series?.map((p) => p.close) || [];
  const last = closes[closes.length - 1];
  const benchCloses = data?.benchmark?.closes || [];
  const b0 = benchCloses.find((v) => v != null);
  const b1 = [...benchCloses].reverse().find((v) => v != null);
  const benchRet = b0 && b1 ? ((b1 - b0) / b0) * 100 : null;
  const distFromHigh = s ? ((s.end_price - s.high) / s.high) * 100 : null;

  const qty = Number(position?.quantity) || 0;
  const cost = Number(position?.avgPrice) || 0;
  const posValue = qty && last ? qty * last : null;
  const sinceCost = cost && last ? ((last - cost) / cost) * 100 : null;

  const ask = () => {
    onClose();
    const label = PERIODS.find(([k]) => k === selPeriod)?.[1] || '6M';
    const facts = s
      ? ` It is ${fmtPct(s.change_percent)} over ${label}${benchRet != null ? ` against ${fmtPct(benchRet)} for the S&P 500` : ''}, with a worst drawdown of ${fmtPct(s.max_drawdown_pct, { sign: false })}${s.volatility_pct != null ? ` and annualised volatility of ${fmtNum(s.volatility_pct, 1)}%` : ''}.`
      : '';
    navigate('/chat', { state: { prefill: `What has driven ${sym} over the last ${label}, and does it change anything for me?${facts}` } });
  };

  return (
    <div className="cm-overlay" onClick={onClose} role="presentation">
      <div className="cm" role="dialog" aria-modal="true" aria-label={`${sym} price chart`} onClick={(e) => e.stopPropagation()}>
        <div className="cm-head">
          <div>
            <div className="cm-title-row">
              <h2 className="dd-serif">{sym}</h2>
              {s && <span className={`cm-change dd-mono ${signClass(s.change_percent)}`}>{fmtPct(s.change_percent)} · {PERIODS.find(([k]) => k === selPeriod)?.[1]}</span>}
            </div>
            <div className="dd-eyebrow">{title || 'Price chart'}{data ? ` · ${cur}` : ''}</div>
          </div>
          <div className="cm-price">
            {s && <span className="dd-serif dd-num cm-last">{fmtMoney(s.end_price, cur, s.end_price >= 1000 ? 0 : 2)}</span>}
            <button type="button" className="cm-close" onClick={onClose} aria-label="Close">×</button>
          </div>
        </div>

        {qty > 0 && (
          <div className="cm-position dd-mono">
            <span>You hold {fmtNum(qty, 0)} sh{posValue != null ? ` · ${fmtMoney(posValue, cur, 0)}` : ''}</span>
            {cost > 0 && <span>avg cost {fmtMoney(cost, cur, 2)}{sinceCost != null && <span className={signClass(sinceCost)}> · {fmtPct(sinceCost)} since cost</span>}</span>}
            {!cost && <span className="dd-muted">Add your average cost in Settings to see the cost line.</span>}
          </div>
        )}

        <div className="cm-controls">
          <div className="cm-seg" role="group" aria-label="Period">
            {PERIODS.map(([k, label]) => <button key={k} type="button" className={k === selPeriod ? 'on' : ''} onClick={() => setSelPeriod(k)} disabled={loading}>{label}</button>)}
          </div>
          <div className="cm-seg" role="group" aria-label="Chart type">
            <button type="button" className={type === 'line' ? 'on' : ''} onClick={() => setType('line')}>Line</button>
            <button type="button" className={type === 'candles' ? 'on' : ''} onClick={() => setType('candles')}>Candles</button>
          </div>
          <label className="cm-toggle dd-mono">
            <input type="checkbox" checked={bench} onChange={(e) => setBench(e.target.checked)} disabled={!data?.benchmark} />
            vs S&amp;P 500
          </label>
        </div>

        <div className="cm-body">
          {loading && !data && <div className="cm-state dd-mono dd-muted">Drawing {sym}…</div>}
          {error && <div className="cm-state"><span className="dd-loss">{error}</span><button type="button" className="dd-btn-ghost" onClick={load}>Try again</button></div>}
          {data && !error && (
            <div className={loading ? 'cm-dim' : ''}>
              <PriceChart series={data.series} benchmark={data.benchmark} currency={cur} type={type} showBenchmark={bench} position={qty > 0 ? position : null} height={320} ariaLabel={`${sym} ${selPeriod} price chart`} />
            </div>
          )}
        </div>

        {s && (
          <div className="cm-stats">
            <div><div className="dd-eyebrow">Period</div><div className={`dd-serif dd-num ${signClass(s.change_percent)}`}>{fmtPct(s.change_percent)}</div></div>
            <div><div className="dd-eyebrow">vs S&amp;P 500</div><div className={`dd-serif dd-num ${signClass(benchRet != null ? s.change_percent - benchRet : 0)}`}>{benchRet != null ? fmtPct(s.change_percent - benchRet) : '—'}</div></div>
            <div><div className="dd-eyebrow">Range</div><div className="dd-serif dd-num">{fmtMoney(s.low, cur, s.low >= 1000 ? 0 : 2)} – {fmtMoney(s.high, cur, s.high >= 1000 ? 0 : 2)}</div></div>
            <div><div className="dd-eyebrow">Off the high</div><div className="dd-serif dd-num dd-loss">{fmtPct(distFromHigh)}</div></div>
            <div><div className="dd-eyebrow">Worst drawdown</div><div className="dd-serif dd-num dd-loss">{fmtPct(s.max_drawdown_pct)}</div></div>
            <div><div className="dd-eyebrow">Volatility · ann.</div><div className="dd-serif dd-num">{s.volatility_pct != null ? `${fmtNum(s.volatility_pct, 1)}%` : '—'}</div></div>
          </div>
        )}

        <div className="cm-foot">
          <span className="dd-mono dd-muted">Daily closes · Yahoo Finance{data?.usage?.daily_limit ? ` · ${data.usage.remaining} of ${data.usage.daily_limit} charts left today` : ''}</span>
          <div className="cm-foot-actions">
            <button type="button" className="dd-btn-ghost" onClick={ask}>Ask the analyst about {sym}</button>
            <button type="button" className="dd-btn" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}
